import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import { t, type IntlService } from 'ember-intl'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import ribbonGold from '@images/giraffe/ribbon-gold.svg'
import ribbonSilver from '@images/giraffe/ribbon-silver.svg'
import ribbonBronze from '@images/giraffe/ribbon-bronze.svg'
import type { AwardsEarnedData } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned'

export interface RibbonViewModel {
  value: string | number
  label: string
  legend?: string
  legendClassNames?: string
  src: string
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    data: AwardsEarnedData
    withLegend?: boolean
  }
}

export class AwardsEarnedRibbons extends Component<Signature> {
  @service intl!: IntlService

  get goldRibbon(): RibbonViewModel {
    return {
      value: formatNumber(this.args.data.gold, 0),
      label: this.intl.t('awards.gold'),
      legend: this.args.withLegend ? '91-100%' : undefined,
      legendClassNames: this.args.withLegend ? 'bg-medal-gold' : undefined,
      src: ribbonGold,
    }
  }

  get silverRibbon(): RibbonViewModel {
    return {
      value: formatNumber(this.args.data.silver, 0),
      label: this.intl.t('awards.silver'),
      legend: this.args.withLegend ? '80-90%' : undefined,
      legendClassNames: this.args.withLegend ? 'bg-medal-silver' : undefined,
      src: ribbonSilver,
    }
  }

  get bronzeRibbon(): RibbonViewModel {
    return {
      value: formatNumber(this.args.data.bronze, 0),
      label: this.intl.t('awards.bronze'),
      legend: this.args.withLegend ? '65-79%' : undefined,
      legendClassNames: this.args.withLegend ? 'bg-medal-bronze' : undefined,
      src: ribbonBronze,
    }
  }

  <template>
    <div class="mx-auto flex max-w-md items-center justify-center gap-2" data-test-awards-earned-ribbons ...attributes>
      <Ribbon class="mt-12 w-1/3" @ribbon={{this.goldRibbon}} />
      <Ribbon class="-mt-12 w-1/3" @ribbon={{this.silverRibbon}} />
      <Ribbon class="mt-12 w-1/3" @ribbon={{this.bronzeRibbon}} />
    </div>
  </template>
}

interface RibbonSignature {
  Element: HTMLDivElement
  Args: {
    ribbon: RibbonViewModel
  }
}

const Ribbon: TOC<RibbonSignature> = <template>
  <div ...attributes>
    <div class="relative">
      <img src={{@ribbon.src}} class="drop-shadow-[7px_7px_1px_#E0E0E0]" alt={{t "awards.ribbon"}} />
      <div class="absolute top-[20%] left-1/2 -translate-x-1/2 transform text-2xl leading-none font-bold text-black">
        {{@ribbon.value}}
      </div>
      <div class="absolute top-[40%] left-1/2 -translate-x-1/2 transform text-xl leading-none font-bold text-white">
        {{@ribbon.label}}
      </div>
    </div>
    {{#if @ribbon.legend}}
      <div class="text-center font-bold">
        <div class="mx-auto h-4 w-4 rounded-full {{@ribbon.legendClassNames}}"></div>
        {{@ribbon.label}}
        <br />
        {{@ribbon.legend}}
      </div>
    {{/if}}
  </div>
</template>
